import React, { type CSSProperties, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { useResize } from '@ev-tech/ui-core-components/utils';
import { Link } from '@ev-tech/ui-core-components/Link';

import { PlusNaked } from '@/icons/small/PlusNaked';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { MinusNaked } from '@/components/common/Icons/MinusNaked';

import styles from './ExpandableCopy.module.css';

export interface IExpandableCopyProps {
  className?: string;
  content: React.ReactNode;
  initiallyOpen?: boolean;
  buttonLabels?: { open: string; close: string };
  lines?: number;
  'data-testid'?: string;
  onExpandButtonClick?: (isOpen: boolean) => void;
}

export const ExpandableCopy = ({
  className,
  content,
  initiallyOpen = false,
  buttonLabels: buttonLabelsProp,
  lines = 5,
  onExpandButtonClick,
  'data-testid': dataTestId,
}: IExpandableCopyProps) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACE.property_expose);

  const contentRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const [isReadMoreVisible, setIsReadMoreVisible] = useState(true);
  const width = useResize();

  const buttonLabels = buttonLabelsProp ?? {
    open: t('search.expose.propertyDetails.label.showLess'),
    close: t('search.expose.propertyDetails.label.showMore'),
  };

  const handleOnClickToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const top = contentRef.current?.getBoundingClientRect().top || 0;
    if (!isOpen && top < 0) {
      contentRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
    onExpandButtonClick?.(isOpen);
    setIsOpen((prevState) => !prevState);
  };

  useEffect(() => {
    if (contentRef.current) {
      setIsReadMoreVisible(contentRef.current.scrollHeight > contentRef.current.clientHeight);
    }
  }, [contentRef, width, isReadMoreVisible, lines]);

  return (
    <div className={className} data-testid={dataTestId}>
      <div className={styles.copyWrapper} ref={contentRef} data-open={isOpen} style={{ '--lines': lines } as CSSProperties}>
        {content}
      </div>
      {isReadMoreVisible && (
        <Link
          label={isOpen ? buttonLabels.open : buttonLabels.close}
          as="button"
          size="medium"
          icon={isOpen ? <MinusNaked className={styles.icon} /> : <PlusNaked className={styles.icon} />}
          iconPosition="right"
          onClick={handleOnClickToggle}
        />
      )}
    </div>
  );
};
